export const PAGING_ITEMS_PER_PAGE = 20;

export const POLLING_INTERVAL_SHORT = 1000; // milliseconds
export const POLLING_INTERVAL_VISIBLE = 5000; // milliseconds
export const POLLING_INTERVAL_LONG = 60000; // milliseconds

export const TRUNCATE_TEXT_LENGTH = 200; // to avoid too long texts in table listings

export const APP_VERSION = process.env.VUE_APP_VERSION;

// custom VUE_APP_DEBUG_MODE or default Vue`s VUE_APP_DEBUG
// if VUE_APP_DEBUG_MODE is not set, use VUE_APP_DEBUG
export const IS_DEBUG_MODE =
  process.env.VUE_APP_DEBUG_MODE !== undefined
    ? process.env.VUE_APP_DEBUG_MODE.toLowerCase() === 'true' ||
      process.env.VUE_APP_DEBUG_MODE === '1'
    : process.env.VUE_APP_DEBUG;

// --- Select options ---
export interface SelectOption<T extends string | null> {
  text: string;
  value: T;
  disabled?: boolean;
}

// --- User roles: user, admin, developer
export const USER_ROLES = {
  USER: 'user',
  ADMIN: 'admin',
  DEVELOPER: 'developer',
} as const;

// --- Organization user roles and modes: member, admin, owner
export const ORGANIZATION_USER_ROLES = {
  MEMBER: 'member',
  ADMIN: 'admin',
  OWNER: 'owner',
} as const;

export const ORGANIZATION_MEMBERSHIP_MODES = {
  MEMBER: 'member',
  ADMIN: 'admin',
} as const;

export type OrganizationUserRoleType =
  (typeof ORGANIZATION_USER_ROLES)[keyof typeof ORGANIZATION_USER_ROLES];
export type OrganizationUserRoleSelectOptionType =
  SelectOption<OrganizationUserRoleType>;
export const OrganizationUserRoleSelectOptions: ReadonlyArray<OrganizationUserRoleSelectOptionType> =
  (
    Object.values(
      ORGANIZATION_USER_ROLES
    ) as readonly OrganizationUserRoleType[]
  ).map((value) => ({
    value,
    text: value.charAt(0).toUpperCase() + value.slice(1),
  }));

export type OrganizationMembershipModeType =
  (typeof ORGANIZATION_MEMBERSHIP_MODES)[keyof typeof ORGANIZATION_MEMBERSHIP_MODES];
export type OrganizationMembershipModeSelectOptionType =
  SelectOption<OrganizationMembershipModeType>;
export const OrganizationMembershipModeSelectOptions: ReadonlyArray<OrganizationMembershipModeSelectOptionType> =
  (
    Object.values(
      ORGANIZATION_MEMBERSHIP_MODES
    ) as readonly OrganizationMembershipModeType[]
  ).map((value) => ({
    value,
    text: value.charAt(0).toUpperCase() + value.slice(1),
  }));

// --- Sharing user roles and modes: viewer, editor, owner
export const SHARING_USER_ROLES = {
  VIEWER: 'viewer',
  EDITOR: 'editor',
  OWNER: 'owner',
} as const;

export type SharingUserRoles =
  (typeof SHARING_USER_ROLES)[keyof typeof SHARING_USER_ROLES];

export const SHARING_MODES = {
  VIEWER: 'viewer',
  EDITOR: 'editor',
} as const;

export type SharingModeType =
  (typeof SHARING_MODES)[keyof typeof SHARING_MODES];
export type SharingModeSelectOptionType = SelectOption<SharingModeType>;
export const SharingModeSelectOptions: ReadonlyArray<SharingModeSelectOptionType> =
  (Object.values(SHARING_MODES) as readonly SharingModeType[]).map((value) => ({
    value,
    text: value.charAt(0).toUpperCase() + value.slice(1),
  }));

// --- Upload Modes: WEB vs. FTP ---
export const UPLOAD_MODES = {
  WEB: 'WEB',
  FTP: 'FTP',
  BASESPACE: 'BASESPACE',
  WASABI: 'WASABI',
} as const;

export type UploadModeType = (typeof UPLOAD_MODES)[keyof typeof UPLOAD_MODES];
export type UploadModeSelectOptionType = SelectOption<UploadModeType>;
export const UploadModeSelectOptions: ReadonlyArray<UploadModeSelectOptionType> =
  (Object.values(UPLOAD_MODES) as readonly UploadModeType[]).map((value) => ({
    value,
    text: value.charAt(0).toUpperCase() + value.slice(1),
    disabled: false,
  }));

// --- Bucket data types ---
export const BUCKET_DATA_TYPES = {
  UNKNOWN: 'unknown',
  INPUT: 'input',
  OUTPUT: 'output',
  SYSTEM: 'system',
} as const;

// --- Coupon types ---
export const COUPON_TYPE_NAMES = {
  STORAGE: 'storage',
  COMPUTE: 'compute',
} as const;

export const COUPON_UNITS = {
  SAMPLES: 'samples',
} as const;

export type CouponTypeName =
  (typeof COUPON_TYPE_NAMES)[keyof typeof COUPON_TYPE_NAMES];
export type CouponUnitsName = (typeof COUPON_UNITS)[keyof typeof COUPON_UNITS];

export const COMPUTE_TASK_TYPES = {
  BCL_TO_FASTQ: 'BCL_TO_FASTQ',
  COMPUTE_PIPELINE: 'COMPUTE_PIPELINE',
  DATA_PIPELINE: 'DATA_PIPELINE',
  FTP_TRANSFER: 'FTP_TRANSFER',
  BASESPACE_TRANSFER: 'BASESPACE_TRANSFER',
  NEXTFLOW_PIPELINE: 'NEXTFLOW_PIPELINE',
  WASABI_TRANSFER: 'WASABI_TRANSFER',
} as const;

export const COMPUTE_TASK_STATUS = {
  PENDING: 'PENDING',
  CREATING: 'CREATING',
  RUNNING: 'RUNNING',
  ABORTING: 'ABORTING',
  ABORTED: 'ABORTED',
  STOPPED: 'STOPPED',
  STALLED: 'STALLED',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
} as const;

// VM lifecycle options
export const VM_LIFECYCLES = {
  AUTO: null, // default – delete the VM on every exit path
  SUSPEND: 'SUSPEND', // suspend the VM on every exit path
  KEEP: 'KEEP', // always keep running
  SUSPEND_ON_FAILURE: 'SUSPEND_ON_FAILURE', // suspend only on failure / abort, delete otherwise
  KEEP_ON_FAILURE: 'KEEP_ON_FAILURE', // keep running on failure / abort, delete otherwise
} as const;

export type VmLifecycleType =
  (typeof VM_LIFECYCLES)[keyof typeof VM_LIFECYCLES];
export type VmLifecycleSelectOptionType = SelectOption<VmLifecycleType>;
export const VmLifecycleSelectOptions: readonly SelectOption<VmLifecycleType>[] =
  [
    { text: 'Auto (delete on every exit path)', value: VM_LIFECYCLES.AUTO },
    { text: 'Suspend on every exit path', value: VM_LIFECYCLES.SUSPEND },
    { text: 'Keep running', value: VM_LIFECYCLES.KEEP },
    {
      text: 'Suspend on failure/abort, delete otherwise',
      value: VM_LIFECYCLES.SUSPEND_ON_FAILURE,
    },
    {
      text: 'Keep running on failure/abort, delete otherwise',
      value: VM_LIFECYCLES.KEEP_ON_FAILURE,
    },
  ] as const;
